/** Platform accents for subscribe UI (badges & selected module rings). */
export const PROVIDER_THEME = {
  xero: {
    primary: "#0ea5e9",
    light: "#f0f9ff",
    ring: "ring-sky-400",
    badge: "bg-sky-50 text-sky-700 border-sky-200",
    label: "Xero",
  },
  qbo: {
    primary: "#16a34a",
    light: "#f0fdf4",
    ring: "ring-green-500",
    badge: "bg-green-50 text-green-800 border-green-200",
    label: "QuickBooks Online",
  },
};

export const SUBSCRIBE_MODULES = {
  account: {
    key: "accounts",
    label: "Account",
    description:
      "Essential account data, journals & attachments — always included",
    price: "$9.95/month",
    required: true,
    icon: "/assets/icons/organization.svg",
  },
  files: {
    key: "files",
    label: "Files",
    description: "Backup all files and documents from your ledger",
    price: "+$4/month",
    icon: "/assets/icons/file.svg",
  },
  history_and_notes: {
    key: "history_and_notes",
    label: "History and Notes",
    description: "Preserve your transaction history and notes",
    price: "+$4/month",
    icon: "/assets/icons/schedule.svg",
  },
  payroll: {
    key: "payroll",
    label: "Payroll",
    description: "Secure backup of payroll data and records",
    price: "+$0/month",
    icon: "/assets/icons/payment.svg",
  },
};

export const getProviderTheme = (provider) =>
  PROVIDER_THEME[provider] || PROVIDER_THEME.xero;

export const getSubscribeIntro = (provider) => {
  if (provider === "qbo") {
    return "Your QuickBooks Online plan includes the Account module.";
  }
  return "Your plan includes the Account module. Select additional modules below.";
};

/** Align with wowzer_normalize_xero_country_code (AU|CA|US|GB|NZ). */
export const normalizeXeroCountryCode = (countryCode) => {
  const c = String(countryCode ?? "")
    .trim()
    .toUpperCase();
  if (c === "UK") return "GB";
  if (["AU", "CA", "US", "GB", "NZ"].includes(c)) return c;
  return c;
};

/** Xero Payroll backup is available for Australia, United Kingdom, and New Zealand orgs. */
export const isPayrollSubscribeEligible = (countryCode) => {
  const c = normalizeXeroCountryCode(countryCode);
  return c === "AU" || c === "GB" || c === "NZ";
};

/** @returns {number} Monthly total in USD */
export const computeSubscribeMonthlyTotal = (formData) => {
  if (!formData?.accounts || formData?.skipped) {
    return 0;
  }
  let total = 9.95;
  if (formData.files) total += 4;
  if (formData.history_and_notes) total += 4;

  const retentionDays = Number(formData.retention_days) || 7;
  const extraRetentionDays = Math.max(0, retentionDays - 7);
  total += extraRetentionDays * 2.0;

  if (formData.coupon_discount) {
    if (formData.coupon_discount.percent_off != null) {
      total = total * (1 - Number(formData.coupon_discount.percent_off) / 100);
    } else if (formData.coupon_discount.amount_off != null) {
      total = Math.max(0, total - Number(formData.coupon_discount.amount_off));
    }
  }

  return total;
};

export const getDefaultOrgPlan = (org = null) => {
  const isExisting = Boolean(
    org && (Number(org.org_status) === 1 || Number(org.subscription) === 1)
  );
  const initialFiles = org && (org.files == 1 || org.files === true);
  const initialHN = org && (org.history_and_notes == 1 || org.history_and_notes === true);
  const initialPayroll = org && (org.payroll == 1 || org.payroll === true);
  const initialRetention = Number(org?.retention_days || org?.duration || 7) || 7;

  return {
    skipped: false,
    accounts: true,
    files: initialFiles,
    history_and_notes: initialHN,
    payroll: initialPayroll,
    retention_days: initialRetention,
    coupon_code: "",
    coupon_discount: null,
    isExistingSubscription: isExisting,
    initialState: isExisting
      ? {
          files: initialFiles,
          history_and_notes: initialHN,
          payroll: initialPayroll,
          retention_days: initialRetention,
        }
      : null,
  };
};

export const hasPlanAddonsChanged = (plan) => {
  if (!plan?.isExistingSubscription || !plan?.initialState) return false;
  const init = plan.initialState;
  const curFiles = Boolean(plan.files);
  const curHN = Boolean(plan.history_and_notes);
  const curPayroll = Boolean(plan.payroll);
  const curRetention = Number(plan.retention_days) || 7;

  return (
    curFiles !== init.files ||
    curHN !== init.history_and_notes ||
    curPayroll !== init.payroll ||
    curRetention !== init.retention_days
  );
};

/** @returns {'pending'|'included'|'skipped'} */
export const getOrgPlanStatus = (plan) => {
  if (!plan || plan.skipped || !plan.accounts) {
    return "skipped";
  }
  return "included";
};

export const formatPlanModuleSummary = (plan, provider = "xero", countryCode) => {
  if (!plan?.accounts || plan.skipped) {
    return "—";
  }
  const parts = ["Account"];
  if (provider !== "qbo") {
    if (plan.files) parts.push("Files");
    if (plan.history_and_notes) parts.push("History & Notes");
    if (plan.payroll && isPayrollSubscribeEligible(countryCode)) {
      parts.push("Payroll");
    }
  }
  const retentionDays = Number(plan.retention_days) || 7;
  if (retentionDays > 7) {
    parts.push(`${retentionDays}-day retention`);
  }
  return parts.join(", ");
};

/**
 * Whether an org can appear in the multi-subscribe wizard.
 * Any organization that does not require re-authentication can be managed/subscribed.
 */
export const isOrgEligibleForSubscribe = (org) => {
  if (!org) return false;
  if (org.need_re_authentication == 1) {
    return false;
  }
  return true;
};

export const getOrgKey = (org) => {
  if (!org) return "";
  if (org.statusKey) return String(org.statusKey);
  const provider = org.provider ?? "xero";
  return `${provider}-${org.id}`;
};

export const buildInitialOrgPlans = (orgs, selectedOrgId = null) => {
  const plans = {};
  const isArrayTarget = Array.isArray(selectedOrgId);
  const targetSet = isArrayTarget
    ? new Set(selectedOrgId.map(String))
    : null;
  const hasTarget = targetSet
    ? targetSet.size > 0
    : selectedOrgId != null && selectedOrgId !== "";

  const checkIsTarget = (org) => {
    const idStr = String(org.id);
    const keyStr = getOrgKey(org);
    const statusStr = String(org.statusKey);
    if (targetSet) {
      return (
        targetSet.has(idStr) ||
        targetSet.has(keyStr) ||
        targetSet.has(statusStr)
      );
    }
    const singleStr = String(selectedOrgId);
    return idStr === singleStr || keyStr === singleStr || statusStr === singleStr;
  };

  const hasMatchingTarget = hasTarget && orgs.some(checkIsTarget);

  orgs.forEach((org) => {
    const key = getOrgKey(org);
    if (hasMatchingTarget) {
      const isTarget = checkIsTarget(org);
      plans[key] = isTarget
        ? getDefaultOrgPlan(org)
        : {
            ...getDefaultOrgPlan(org),
            accounts: false,
            skipped: true,
          };
    } else {
      plans[key] = getDefaultOrgPlan(org);
    }
  });
  return plans;
};

export const buildBulkSubscribePayload = (orgs, orgPlans) =>
  orgs.map((org) => {
    const key = getOrgKey(org);
    const plan = orgPlans[key] ?? orgPlans[org.id] ?? getDefaultOrgPlan(org);
    const hasAccount = !!plan.accounts && !plan.skipped;
    return {
      org_id: Number(org.id),
      provider: org.provider ?? "xero",
      orgKey: key,
      isExistingSubscription: !!plan.isExistingSubscription,
      hasChanged: hasPlanAddonsChanged(plan),
      skipped: plan.skipped || !plan.accounts,
      accounts: hasAccount,
      files: hasAccount && !!plan.files,
      history_and_notes: hasAccount && !!plan.history_and_notes,
      payroll: hasAccount && !!plan.payroll,
      retention_days: hasAccount ? (Number(plan.retention_days) || 7) : 7,
      coupon_code:
        hasAccount && plan.coupon_code
          ? String(plan.coupon_code).trim()
          : null,
    };
  });
